import {testIdProps} from '@github-ui/test-id-props'
import type {Repository} from '@github-ui/current-repository'
import {modelsCatalogPath} from '@github-ui/paths'
import {useState, useRef, useMemo} from 'react'
import {
  ActionList,
  ActionMenu,
  IconButton,
  Button,
  SelectPanel,
  Link,
  useResponsiveValue,
  type ResponsiveValue,
} from '@primer/react'
import {ChevronDownIcon, InfoIcon} from '@primer/octicons-react'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useQuery} from '@github-ui/react-query'
import type {Model} from '@github-ui/marketplace-common'
import {SidebarSelectionOptions, type GettingStartedPayload, type ModelItemInput} from '../../../types'
import {normalizeModelPublisher} from '../../../utils/normalize-model-strings'
import {PublisherAvatar} from '../../../components/PublisherAvatar'
import ModelInfo from '../../../components/ModelDetailsSidebar/ModelInfo'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useLayoutEffect} from '@github-ui/use-layout-effect'
import {userHasAccessToModel} from '../../../utils/model-access'
import styles from './ModelSwitcher.module.css'
import {useLocation} from 'react-router-dom'
import type {ActionListItemInput as ItemInput} from '@primer/react/deprecated'
import {useFilteredModels} from '@github-ui/github-models-repo/UseFilteredModels'

export default function ModelSwitcher({
  model,
  onComparisonMode,
  onSelect,
  handleSetSidebarTab,
  variant,
}: {
  model?: Model
  onComparisonMode: boolean
  onSelect(model: Model): void
  handleSetSidebarTab: (newTab: SidebarSelectionOptions) => void
  variant?: 'default' | 'compare_button'
}) {
  const {repository} = useAppPayload<{repository: Repository}>()
  const responsiveButtonSizes: ResponsiveValue<'small' | 'medium'> = {narrow: 'medium', regular: 'small'}

  const linkSize = useResponsiveValue(responsiveButtonSizes, 'small')

  const {restrictedModels} = useRoutePayload<GettingStartedPayload>()
  const [showPanel, setShowPanel] = useState(false)
  const [filter, setFilter] = useState('')

  const location = useLocation()
  const isOnMarketplace = location.pathname.includes('/marketplace/models')

  const anchorRef = useRef<HTMLButtonElement>(null)
  const selectedModelElement = useRef<HTMLLIElement | null>(null)

  const {data: marketplaceModels, isLoading: isLoadingMarketplaceModels} = useQuery<Model[]>({
    queryKey: ['github-models', 'models'],
    initialData: [],
    async queryFn() {
      const res = await verifiedFetchJSON('/marketplace/models')
      if (!res.ok) throw new Error(await res.text())
      return res.json()
    },
    enabled: isOnMarketplace,
  })

  const {availableModels: repoModels, isLoadingModels: isLoadingRepoModels} = useFilteredModels(
    repository?.ownerLogin || '',
    repository?.name || '',
    restrictedModels,
  )

  const fetchedAvailableModels = useMemo(() => {
    return isOnMarketplace ? marketplaceModels : repoModels
  }, [isOnMarketplace, marketplaceModels, repoModels])

  const items = useMemo<ModelItemInput[]>(() => {
    return fetchedAvailableModels.map(m => {
      const item = {
        text: m.friendly_name,
        leadingVisual() {
          return (
            <PublisherAvatar logoUrl={m.logo_url} darkModeIcon={m.dark_mode_icon} publisher={m.publisher} size={20} />
          )
        },
        item: m,
        id: m.id,
        selected: m.id === model?.id,
        ref: m.id === model?.id ? selectedModelElement : null,
      }
      return item
    })
  }, [fetchedAvailableModels, model])

  const selected = useMemo<ModelItemInput | undefined>(() => {
    return items.find(i => i.selected)
  }, [items])

  useLayoutEffect(() => {
    if (showPanel) {
      requestAnimationFrame(() => {
        selectedModelElement.current?.scrollIntoView({
          behavior: 'smooth',
        })
      })
    }
  }, [showPanel])

  const filteredModels = items.filter(
    m =>
      m.item.task === 'chat-completion' &&
      (m.item.friendly_name.toLowerCase().includes(filter.toLowerCase()) ||
        normalizeModelPublisher(m.item.publisher).toLowerCase().includes(filter.toLowerCase())) &&
      userHasAccessToModel(m.item.name, restrictedModels),
  )

  return (
    <div className="d-flex flex-items-center">
      {variant === 'compare_button' ? (
        <Button
          variant="default"
          ref={anchorRef}
          onClick={() => setShowPanel(true)}
          aria-label="Select model to compare"
          {...testIdProps('compare-model-button')}
        >
          Compare
        </Button>
      ) : (
        <Button
          variant={model ? 'default' : 'primary'}
          ref={anchorRef}
          onClick={() => setShowPanel(true)}
          aria-label="Switch model"
          leadingVisual={() => {
            if (!model) return null
            return (
              <PublisherAvatar
                logoUrl={model.logo_url}
                darkModeIcon={model.dark_mode_icon}
                publisher={model.publisher}
                size={18}
              />
            )
          }}
          trailingVisual={() => <ChevronDownIcon />}
          className={model && 'bgColor-default'}
          {...testIdProps('model-friendly-name')}
        >
          <div className={styles.modelName}>
            <span className={model && 'fgColor-muted'}>Model:</span> {model ? model.friendly_name : 'Select a Model'}
          </div>
        </Button>
      )}
      <SelectPanel
        renderAnchor={null}
        loading={isOnMarketplace ? isLoadingMarketplaceModels : isLoadingRepoModels}
        open={showPanel}
        onOpenChange={setShowPanel}
        anchorRef={anchorRef}
        title={variant === 'compare_button' ? 'Select model' : 'Switch model'}
        onSelectedChange={(selectedModel: ItemInput | undefined) => {
          if (selectedModel && selectedModel.item) {
            onSelect(selectedModel.item as Model)
          } else if (variant === 'compare_button' && !selectedModel && model) {
            onSelect(model)
          }
        }}
        selected={selected}
        items={filteredModels}
        onFilterChange={setFilter}
        overlayProps={{width: 'medium', height: 'large'}}
        textInputProps={{
          ['aria-label']: 'Filter models',
        }}
        placeholderText="Search"
        // TODO: replace with SecondaryAction when available
        footer={
          isOnMarketplace ? (
            <Button
              as={Link}
              size={linkSize}
              variant="invisible"
              block
              className={styles.SmallText}
              href={modelsCatalogPath()}
            >
              View all models
            </Button>
          ) : undefined
        }
        onCancel={() => setShowPanel(false)}
      />
      {model && onComparisonMode && (
        <div className="ml-2 hide-sm">
          <ActionMenu>
            <ActionMenu.Anchor>
              <IconButton icon={InfoIcon} aria-label="Show model info" {...testIdProps('model-info-button')} />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay width="xlarge" side="outside-left">
              <ActionList>
                <div className="border-bottom borderColor-default px-3 pt-2 pb-3" {...testIdProps('model-info-menu')}>
                  <h1 className="h4">Details</h1>
                </div>
                <ModelInfo headingLevel="h2" model={model} renderAs="listitem" />
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </div>
      )}
      {model && variant !== 'compare_button' && (
        <div className="hide-lg hide-xl ml-2">
          <IconButton
            icon={InfoIcon}
            aria-label="Show model info"
            onClick={() => handleSetSidebarTab(SidebarSelectionOptions.DETAILS)}
          />
        </div>
      )}
    </div>
  )
}

try{ ModelSwitcher.displayName ||= 'ModelSwitcher' } catch {}